import React, { useEffect, useState } from "react";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Power, ShieldCheck, ShieldAlert, Pencil, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Sucursales() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const [sucs, setSucs] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  // Estado del diálogo de reinicio
  const [resetOpen, setResetOpen] = useState(false);
  const [resetSuc, setResetSuc] = useState(null);
  const [resetConf, setResetConf] = useState("");
  const [resetConfActivas, setResetConfActivas] = useState("");
  const [resetRequiereActivas, setResetRequiereActivas] = useState(false);
  const [resetOrdActivas, setResetOrdActivas] = useState(0);
  const [resetLoading, setResetLoading] = useState(false);

  const load = async () => {
    const [s, c] = await Promise.all([api.get("/admin/sucursales"), api.get("/admin/clientes")]);
    setSucs(s.data); setClientes(c.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      cliente_id: s.cliente_id,
      nombre: s.nombre || "",
      direccion: s.direccion || "",
      telefono: s.telefono || "",
      fecha_vencimiento: s.fecha_vencimiento ? s.fecha_vencimiento.slice(0, 10) : "",
      estatus_licencia: s.estatus_licencia || "PRUEBA",
      active: s.active,
    });
    setOpen(true);
  };

  const onSave = async () => {
    if (!form.nombre?.trim()) return toast.error("Nombre requerido");
    try {
      if (editing?.id) {
        const payload = {
          nombre: form.nombre,
          direccion: form.direccion || null,
          telefono: form.telefono || null,
        };
        await api.patch(`/admin/sucursales/${editing.id}`, payload);
        // Cambio de estado opcional
        if (form.active !== editing.active) {
          if (form.active) await api.post(`/admin/sucursales/${editing.id}/activar`);
          else await api.post(`/admin/sucursales/${editing.id}/suspender`);
        }
        toast.success("Sucursal actualizada correctamente.");
      } else {
        if (!form.cliente_id) return toast.error("Cliente requerido");
        await api.post("/admin/sucursales", form);
        toast.success(`Sucursal "${form.nombre}" creada correctamente.`);
      }
      setOpen(false); setForm({}); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActivo = async (s) => {
    try {
      if (s.active) {
        await api.post(`/admin/sucursales/${s.id}/suspender`);
        toast.success(`Sucursal "${s.nombre}" suspendida.`);
      } else {
        await api.post(`/admin/sucursales/${s.id}/activar`);
        toast.success(`Sucursal "${s.nombre}" activada.`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ============ Reiniciar operación ============
  const openReset = (s) => {
    setResetSuc(s);
    setResetConf("");
    setResetConfActivas("");
    setResetRequiereActivas(false);
    setResetOrdActivas(0);
    setResetOpen(true);
  };

  const closeReset = () => {
    if (resetLoading) return;
    setResetOpen(false);
    setResetSuc(null);
    setResetConf("");
    setResetConfActivas("");
    setResetRequiereActivas(false);
    setResetOrdActivas(0);
  };

  const submitReset = async () => {
    if (!resetSuc?.id) return;
    if (resetConf.trim() !== "REINICIAR") {
      toast.error("Debes escribir exactamente REINICIAR para confirmar.");
      return;
    }
    if (resetRequiereActivas && resetConfActivas.trim() !== "ELIMINAR OPERACIÓN ACTIVA") {
      toast.error("Debes escribir exactamente ELIMINAR OPERACIÓN ACTIVA para confirmar.");
      return;
    }
    setResetLoading(true);
    try {
      const body = { confirmacion: resetConf.trim() };
      if (resetRequiereActivas) body.confirmacion_activas = resetConfActivas.trim();
      const r = await api.post(`/admin/sucursales/${resetSuc.id}/reiniciar-operacion`, body);
      const s = r.data?.summary || {};
      toast.success(
        `Operación reiniciada en "${resetSuc.nombre}". ` +
        `Órdenes: ${s.ordenes_eliminadas || 0} (activas: ${s.ordenes_activas_eliminadas || 0}) · ` +
        `Gastos: ${s.gastos_eliminados || 0} · Auditoría: ${s.audit_logs_ordenes_eliminados || 0} · ` +
        `Counters: ${s.counters_reiniciados || 0}`,
      );
      setResetOpen(false);
      setResetSuc(null);
      setResetConf("");
      setResetConfActivas("");
      setResetRequiereActivas(false);
      setResetOrdActivas(0);
      load();
    } catch (e) {
      // El backend responde 409 cuando hace falta la confirmación adicional.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object" && detail.code === "REQUIERE_CONFIRMACION_ADICIONAL") {
        setResetRequiereActivas(true);
        setResetOrdActivas(detail.ordenes_activas || 0);
        toast.warning(detail.message || "Esta sucursal tiene órdenes activas. Confirma la advertencia adicional.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h2 className="text-3xl font-black tracking-tight">Sucursales</h2>
          <p className="text-sm text-muted-foreground mt-1">Activación, suspensión y vigencia de licencia.</p>
        </div>
        <Button onClick={openCreate} data-testid="new-sucursal-btn"><Plus className="h-4 w-4 mr-2" />Nueva sucursal</Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Editar sucursal" : "Nueva sucursal"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Cliente {editing?.id ? "(no se puede cambiar)" : "*"}</Label>
              <Select value={form.cliente_id || ""} onValueChange={(v) => setForm({ ...form, cliente_id: v })} disabled={!!editing?.id}>
                <SelectTrigger data-testid="sucursal-cliente-select"><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
                <SelectContent>
                  {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre_comercial}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Nombre *</Label><Input value={form.nombre || ""} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="sucursal-nombre-input" /></div>
            <div><Label>Dirección</Label><Input value={form.direccion || ""} onChange={(e) => setForm({ ...form, direccion: e.target.value })} /></div>
            <div><Label>Teléfono</Label><Input value={form.telefono || ""} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>
            {editing?.id && (
              <>
                <div>
                  <Label>Vencimiento de licencia</Label>
                  <Input value={form.fecha_vencimiento || ""} disabled placeholder="Solo lectura" />
                  <p className="text-[10px] text-muted-foreground mt-1">Para extender la licencia, registra un pago en /admin/pagos.</p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer pt-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={!!form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    data-testid="sucursal-active-check"
                  />
                  <span className="text-sm">Sucursal activa</span>
                </label>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={onSave} data-testid="save-sucursal-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sucursal</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Vencimiento</TableHead>
                <TableHead>Licencia</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sucs.map((s) => (
                <TableRow key={s.id} data-testid={`sucursal-row-${s.id}`}>
                  <TableCell className="font-semibold">{s.nombre}</TableCell>
                  <TableCell className="text-muted-foreground">{s.cliente_nombre}</TableCell>
                  <TableCell>{fmtDate(s.fecha_vencimiento)}</TableCell>
                  <TableCell>
                    <span className="px-2 py-1 rounded text-xs font-bold bg-secondary uppercase tracking-wider">{s.estatus_licencia}</span>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-bold inline-flex items-center gap-1 ${s.active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200" : "bg-red-100 text-red-700"}`}>
                      {s.active ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}
                      {s.active ? "ACTIVA" : "SUSPENDIDA"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-sucursal-${s.id}`}>
                      <Pencil className="h-3 w-3 mr-1" />Editar
                    </Button>
                    <Button size="sm" variant={s.active ? "ghost" : "default"} onClick={() => toggleActivo(s)} data-testid={`toggle-sucursal-${s.id}`}>
                      <Power className="h-4 w-4 mr-1" />{s.active ? "Suspender" : "Activar"}
                    </Button>
                    {isSuperAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-950/40"
                        onClick={() => openReset(s)}
                        data-testid={`reset-sucursal-${s.id}`}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />Reiniciar operación
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {sucs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No hay sucursales</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Diálogo de Reiniciar Operación (SUPER_ADMIN) */}
      <Dialog open={resetOpen} onOpenChange={(v) => { if (!v) closeReset(); }}>
        <DialogContent data-testid="reset-sucursal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Reiniciar operación de sucursal
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm leading-relaxed">
              Estás a punto de reiniciar la operación comercial de
              {" "}<span className="font-bold text-foreground">{resetSuc?.nombre}</span>
              {resetSuc?.cliente_nombre ? (
                <> · <span className="text-muted-foreground">{resetSuc.cliente_nombre}</span></>
              ) : null}.
              {" "}Se conservarán <strong>catálogos, usuarios, productos, categorías, mesas, áreas, métodos de pago, inventario base y la configuración general</strong>.
              {" "}Se eliminarán <strong>todas las órdenes, pagos, reembolsos, cancelaciones, gastos y la auditoría operativa de ventas</strong>.
              {" "}Esta acción <strong>no se puede deshacer</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="reset-conf">Escribe <span className="font-mono font-bold">REINICIAR</span> para confirmar</Label>
              <Input
                id="reset-conf"
                value={resetConf}
                onChange={(e) => setResetConf(e.target.value)}
                placeholder="REINICIAR"
                autoComplete="off"
                disabled={resetLoading}
                data-testid="reset-conf-input"
              />
            </div>
            {resetRequiereActivas && (
              <div className="rounded-md border border-amber-500/60 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2">
                <div className="flex items-start gap-2 text-amber-700 dark:text-amber-200 text-sm">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    Esta sucursal tiene <strong>{resetOrdActivas}</strong> orden{resetOrdActivas === 1 ? "" : "es"} activa{resetOrdActivas === 1 ? "" : "s"} o en proceso.
                    {" "}Si continúas, esas órdenes también serán eliminadas como parte del reinicio operativo.
                  </div>
                </div>
                <div>
                  <Label htmlFor="reset-conf-activas" className="text-amber-700 dark:text-amber-200">
                    Escribe <span className="font-mono font-bold">ELIMINAR OPERACIÓN ACTIVA</span> para continuar
                  </Label>
                  <Input
                    id="reset-conf-activas"
                    value={resetConfActivas}
                    onChange={(e) => setResetConfActivas(e.target.value)}
                    placeholder="ELIMINAR OPERACIÓN ACTIVA"
                    autoComplete="off"
                    disabled={resetLoading}
                    data-testid="reset-conf-activas-input"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeReset} disabled={resetLoading} data-testid="reset-cancel-btn">
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={submitReset}
              disabled={
                resetLoading ||
                resetConf.trim() !== "REINICIAR" ||
                (resetRequiereActivas && resetConfActivas.trim() !== "ELIMINAR OPERACIÓN ACTIVA")
              }
              data-testid="reset-confirm-btn"
            >
              {resetLoading ? "Reiniciando…" : "Reiniciar operación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
